import { alpha } from '@mui/material/styles';

export const loginPageStyles = {
  container: (theme) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    position: 'relative',
    overflow: 'hidden',
    background: theme.palette.mode === 'dark'
      ? '#0b1020'
      : '#f5f7ff',
    '@media (max-width:600px)': {
      minHeight: '100dvh',
      padding: theme.spacing(2),
    },
  }),

  // Arkaplan dekoru: yumuşak renkli bloblar + hafif noise, tek yerde
  // toplanmayan, taşkın olmayan modern bir doku.
  backgroundDecor: (theme) => ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-20%',
      right: '-10%',
      width: 540,
      height: 540,
      borderRadius: '50%',
      background: `radial-gradient(circle at 30% 30%, ${alpha(theme.palette.primary.main, 0.35)}, transparent 65%)`,
      filter: 'blur(60px)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-25%',
      left: '-10%',
      width: 620,
      height: 620,
      borderRadius: '50%',
      background: `radial-gradient(circle at 70% 70%, ${alpha(theme.palette.secondary.main, 0.30)}, transparent 65%)`,
      filter: 'blur(70px)',
    },
  }),

  // Ekstra 3. blob orta üst — dolu ve dengeli
  backgroundDecorExtra: (theme) => ({
    position: 'absolute',
    top: '10%',
    left: '30%',
    width: 320,
    height: 320,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${alpha(theme.palette.info.main, 0.20)}, transparent 60%)`,
    filter: 'blur(70px)',
    pointerEvents: 'none',
  }),

  loginCard: (theme) => ({
    width: '100%',
    maxWidth: 480,
    padding: theme.spacing(6, 5.5),
    borderRadius: theme.spacing(3),
    position: 'relative',
    zIndex: 1,
    // Frosted glass etkisi
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.7)
      : alpha('#ffffff', 0.85),
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: `1px solid ${alpha(theme.palette.mode === 'dark' ? '#ffffff' : '#0f172a', 0.08)}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 24px 48px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 24px 60px rgba(15, 23, 42, 0.10), 0 4px 12px rgba(15, 23, 42, 0.05)',
    '@media (max-width:600px)': {
      padding: theme.spacing(4, 3),
    },
  }),

  // Sade logo alani — logo PNG'sinin beyaz arka plani var, bu yuzden
  // ek bir container background eklemiyoruz; kare durup boğmasın.
  logoBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 3,
  },

  logoImg: {
    width: 'auto',
    maxWidth: 280,
    height: 'auto',
    maxHeight: 110,
    objectFit: 'contain',
    display: 'block',
  },

  header: {
    textAlign: 'center',
    marginBottom: 3.5,
  },

  title: {
    fontWeight: 700,
    marginBottom: 1,
    color: 'text.primary',
    letterSpacing: '-0.02em',
    fontSize: { xs: '1.5rem', sm: '1.7rem' },
    // Metne hafif gradient uygula — modern touch
    backgroundImage: (theme) => `linear-gradient(135deg, ${theme.palette.text.primary}, ${alpha(theme.palette.primary.main, 0.85)})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  subtitle: {
    color: 'text.secondary',
    marginBottom: 0,
    fontSize: '0.92rem',
  },

  errorAlert: {
    marginBottom: 2,
    borderRadius: '12px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.15rem',
  },

  textField: (theme) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      transition: 'all .2s ease',
      '& fieldset': {
        borderColor: alpha(theme.palette.divider, 0.6),
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        '& fieldset': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
        '& fieldset': {
          borderColor: theme.palette.primary.main,
        },
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'primary.main',
    },
  }),

  forgotPassword: {
    textDecoration: 'none',
    fontSize: '0.82rem',
    fontWeight: 500,
    color: 'text.secondary',
    '&:hover': {
      color: 'primary.main',
    },
  },

  loginButton: (theme) => ({
    height: 50,
    borderRadius: '12px',
    fontSize: '0.98rem',
    fontWeight: 600,
    textTransform: 'none',
    marginTop: '0.5rem',
    boxShadow: 'none',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    transition: 'all .25s ease',
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    // Disable durumunda çirkin siyah-gri tonu yerine primary'nin
    // yumuşak tinted hali — visually consistent.
    '&.Mui-disabled': {
      background: theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.25)
        : alpha(theme.palette.primary.main, 0.35),
      color: alpha('#ffffff', 0.85),
      boxShadow: 'none',
    },
  }),

  footer: {
    textAlign: 'center',
    marginTop: 3.5,
    fontSize: '0.75rem',
    color: 'text.disabled',
    letterSpacing: '0.02em',
  },
};
